export const ROOT_PARENT_ID = "__root__";

export function createSpecialId() {
  return `special_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function normalizeSpecialTree(items = [], depth = 0) {
  if (!Array.isArray(items) || depth > 2) return [];

  return items
    .filter(Boolean)
    .map((item) => {
      const children = item.children || item.items || item.subLinks;
      const normalized = {
        uid: item.uid || createSpecialId(),
        title: item.title || item.text || item.name || "",
        href: item.href || item.path || "",
        children: normalizeSpecialTree(children, depth + 1),
      };

      if (!normalized.children.length) delete normalized.children;
      return normalized;
    })
    .filter((item) => item.title);
}

export function flattenSpecialTree(items = [], parentId = ROOT_PARENT_ID, level = 0) {
  return items.flatMap((item) => {
    const row = {
      ...item,
      id: item.uid,
      parentId,
      level,
      displayTitle: `${"— ".repeat(level)}${item.title}`,
    };

    return [
      row,
      ...flattenSpecialTree(item.children || [], item.uid, level + 1),
    ];
  });
}

export function stripSpecialTree(items = []) {
  return items.map((item) => {
    const stripped = {
      title: item.title,
      href: item.href,
    };
    if (item.children?.length) {
      stripped.children = stripSpecialTree(item.children);
    }
    return stripped;
  });
}

export function buildSpecialTree(rows = []) {
  const normalizedRows = rows.map((row) => ({
    uid: row.uid || row.id || createSpecialId(),
    title: row.title,
    href: row.href,
    parentId: row.parentId || ROOT_PARENT_ID,
    children: [],
  }));
  const rowById = new Map(normalizedRows.map((row) => [row.uid, row]));
  const root = [];

  normalizedRows.forEach((row) => {
    const parent = row.parentId !== ROOT_PARENT_ID ? rowById.get(row.parentId) : null;
    if (parent) {
      parent.children.push(row);
    } else {
      root.push(row);
    }
  });

  const clean = (items, depth = 0) =>
    items.map((item) => {
      const cleaned = {
        uid: item.uid,
        title: item.title,
        href: item.href,
      };
      if (item.children.length && depth < 2) {
        cleaned.children = clean(item.children, depth + 1);
      }
      return cleaned;
    });

  return clean(root);
}

export function getDescendantIds(rows = [], uid) {
  const descendants = new Set();
  let changed = true;

  while (changed) {
    changed = false;
    rows.forEach((row) => {
      if (
        row.parentId === uid ||
        (row.parentId && descendants.has(row.parentId))
      ) {
        if (!descendants.has(row.uid)) {
          descendants.add(row.uid);
          changed = true;
        }
      }
    });
  }

  return descendants;
}

export function moveSpecialRow(rows = [], uid, direction) {
  const row = rows.find((item) => item.id === uid || item.uid === uid);
  if (!row) return rows;

  const siblings = rows.filter((item) => item.parentId === row.parentId);
  const index = siblings.findIndex((sibling) => sibling.id === uid || sibling.uid === uid);
  const nextIndex = index + direction;
  if (nextIndex < 0 || nextIndex >= siblings.length) return rows;

  const nextSiblings = [...siblings];
  [nextSiblings[index], nextSiblings[nextIndex]] = [
    nextSiblings[nextIndex],
    nextSiblings[index],
  ];

  const siblingIds = new Set(siblings.map((sibling) => sibling.id || sibling.uid));
  const nextRows = [];
  let inserted = false;

  rows.forEach((item) => {
    if (!siblingIds.has(item.id || item.uid)) {
      nextRows.push(item);
      return;
    }

    if (!inserted) {
      nextRows.push(...nextSiblings);
      inserted = true;
    }
  });

  return nextRows;
}
